import React, { useEffect, useState, useRef } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";
import moment from "moment";
import { useParams } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { SessionService } from "../../services/SessionService";
import {
  Session,
  Turn,
  EvaluateResult,
  ToolCall,
} from "../../model_gen/session";
import { ChatCompletionRequestMetadata } from "../../model_gen/chat_metadata";
import { formatCurrency, getMessageContentParts } from "../../utils/FormatUtils";
import { calculateScore } from "./SessionUtils";
import { TestSet } from "../../model_gen/evaluation";
import TestSetContextMenu from "../TestSet/TestSetContextMenu";
import { RequestsService } from "../../services/RequestsService";
import { TestSetService } from "../../services/TestSetService";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { 
  Collapsible, 
  CollapsibleTrigger,  
  CollapsibleContent 
} from "../ui/collapsible";


import { Tooltip } from "../ui/tooltip";
import { MultiSelect, MultiSelectOption } from "../ui/multi-select";
import { Label } from "../ui/label";
import { 
  CopyIcon,
  Loader2Icon,
  CheckCircleIcon,
  InfoIcon,
  PlusIcon,
  HistoryIcon,
  ServerIcon,
  ClockIcon,
  DollarSignIcon,
  ArrowUpIcon,
  ExternalLinkIcon
} from "lucide-react";
import { cn } from "../../lib/utils";

interface SessionOverviewProps {
  sessionId?: string;
  applicationId?: string;
  refreshTrigger?: number;
  requestId?: string;
}

const formatSessionId = (sessionId: string) => {
  if (!sessionId) return "";
  return `...${sessionId.slice(-8)}`;
};

// Theme-aware text formatter to replace formatTextOrJson
const formatResponseContent = (
  text?: string | { [k: string]: unknown }[] | null
): React.ReactNode => {
  const parts = getMessageContentParts(text);
  if (parts.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {parts.map((part, index) => {
        if (part.type === "image") {
          return (
            <img
              key={`${part.value}-${index}`}
              src={part.value}
              alt="Message attachment"
              className="max-w-full rounded-md border border-border"
            />
          );
        }
        let renderedText: React.ReactNode = part.value;
        if (part.value.includes("[") || part.value.includes("{")) {
          try {
            const parsedJson = JSON.parse(part.value);
            const formattedText = JSON.stringify(parsedJson, null, 2);
            renderedText = (
              <pre className="bg-muted/50 border border-border rounded-lg p-3 overflow-x-auto max-w-full">
                <code className="text-foreground text-sm whitespace-pre-wrap break-words">{formattedText}</code>
              </pre>
            );
          } catch (e) {
            renderedText = part.value;
          }
        }
        return <div key={`text-${index}`}>{renderedText}</div>;
      })}
    </div>
  );
};

const SessionOverview: React.FC<SessionOverviewProps> = ({
  sessionId: propSessionId,
  applicationId: propApplicationId,
  refreshTrigger,
  requestId,
}) => {
  const { apiKey } = usePermissions();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [session, setSession] = useState<Session | null>(null);
  const wsRef = useRef<ReconnectingWebSocket | null>(null);
  const lastMessageTimeRef = useRef<Date>(new Date());
  const timelineRef = useRef<HTMLDivElement>(null);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [isLoadingOlder, setIsLoadingOlder] = useState<boolean>(false);
  const [testSetsByAction, setTestSetsByAction] = useState<
    Record<string, TestSet[]>
  >({});
  const [summaryLoading, setSummaryLoading] = useState<{
    [key: string]: boolean;
  }>({});
  const [requestMetadata, setRequestMetadata] = useState<{
    [key: string]: ChatCompletionRequestMetadata;
  }>({});
  const [unselectedIntents, setUnselectedIntents] = useState<Set<string>>(
    new Set()
  );
  const [availableIntents, setAvailableIntents] = useState<string[]>([]);

  // Get parameters from URL if not provided as props
  const { sessionId: paramSessionId, applicationId: paramApplicationId } =
    useParams<{ sessionId: string; applicationId: string }>();

  // Use props if available, otherwise use URL parameters
  const sessionId = propSessionId || paramSessionId;
  const applicationId = propApplicationId || paramApplicationId;

  useEffect(() => {
    if (applicationId && sessionId) {
      loadSession();
    }
    return openWebsocket();
  }, [sessionId, applicationId, apiKey, refreshTrigger]);

  useEffect(() => {
    if (applicationId) {
      loadTestSets();
    }
  }, [applicationId, apiKey]);

  const loadSession = () => {
    SessionService.getSession(
      applicationId,
      sessionId,
      0,
      25,
      setIsLoading,
      apiKey
    ).subscribe(
      (fetchedSession: Session) => {
        setSession(fetchedSession);
      },
      (error: any) => {
        console.error(error);
      }
    );
  };

  const loadOlderRequests = () => {
    setIsLoadingOlder(true);
    const nextPage = page + 1;
    SessionService.getSession(
      applicationId,
      sessionId,
      nextPage,
      25,
      () => {},
      apiKey
    ).subscribe(
      (fetchedSession: Session) => {
        setSession((prevSession) => {
          if (!prevSession) return fetchedSession;
          const existingRequestIds = new Set(
            prevSession.timeline.map((turn) => turn.request?.request_id)
          );
          const newTurns = fetchedSession.timeline.filter(
            (turn) => !existingRequestIds.has(turn.request?.request_id)
          );
          return {
            ...fetchedSession,
            timeline: [...newTurns, ...prevSession.timeline],
          };
        });
        setPage(nextPage);
        setIsLoadingOlder(false);
      },
      (error: any) => {
        console.error(error);
        setIsLoadingOlder(false);
      }
    );
  };

  const openWebsocket = () => {
    closeWsConnection();

    const ws: any = new ReconnectingWebSocket(
      `wss://09hidyy627.execute-api.us-west-2.amazonaws.com/production?key=${encodeURIComponent(
        sessionId
      )}&type=SESSION_UPDATE`
    );
    wsRef.current = ws;

    ws.addEventListener("message", (message: any) => {
      lastMessageTimeRef.current = new Date();
      const data = JSON.parse(message.data);
      if (data.event_type === "SESSION_UPDATE") {
        handleNewSessionTurn(data);
      }
    });

    const checkInterval = setInterval(() => {
      const now = new Date();
      const diffInMinutesWS =
        (now.getTime() - lastMessageTimeRef.current.getTime()) / (1000 * 60);

      if (diffInMinutesWS >= 1) {
        if (wsRef.current) {
          wsRef.current.reconnect(1000, "Manual reconnect");
          lastMessageTimeRef.current = new Date();
        }
      }
    }, 1000 * 5);

    return () => {
      if (checkInterval) clearInterval(checkInterval);
      closeWsConnection();
    };
  };

  const closeWsConnection = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  };

  const handleNewSessionTurn = (data: any) => {
    const turn: Turn = JSON.parse(data.event_data);
    setSession((prevSession) => {
      if (!prevSession) return prevSession;
      const updatedTimeline = [...prevSession.timeline];
      const turnIndex = updatedTimeline.findIndex(
        (t) => t.request?.request_id === turn.request?.request_id
      );
      if (turnIndex !== -1) {
        // Update existing turn
        updatedTimeline[turnIndex] = {
          ...updatedTimeline[turnIndex],
          ...turn,
        };
      } else {
        // Add new turn
        updatedTimeline.unshift(turn);
      }

      // Update available intents with the new turn
      if (turn.request?.action_type) {
        updateAvailableIntents(updatedTimeline);
      }

      return { ...prevSession, timeline: updatedTimeline };
    });
  };

  const removeDuplicateTurns = (timeline: Turn[]): Turn[] => {
    const uniqueTurns = new Map<string, Turn>();
    timeline.forEach((turn) => {
      if (turn.request?.request_id) {
        uniqueTurns.set(turn.request.request_id, turn);
      }
    });
    return Array.from(uniqueTurns.values());
  };

  const sortedTimeline = React.useMemo(() => {
    if (!session?.timeline) return [];
    const uniqueTimeline = removeDuplicateTurns(session.timeline);
    return uniqueTimeline.sort((a, b) => {
      const dateA = new Date(a.request?.date_created || 0);
      const dateB = new Date(b.request?.date_created || 0);
      return dateA.getTime() - dateB.getTime(); // Changed to ascending order
    });
  }, [session?.timeline]);

  const copyRequestId = (requestId: string) => {
    navigator.clipboard.writeText(requestId);
  };

  useEffect(() => {
    if (requestId && session) {
      setHighlightedId(requestId);
      setTimeout(() => {
        const requestElement = document.querySelector(
          `[data-request-id="${requestId}"]`
        );
        if (requestElement) {
          requestElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);

      // Remove highlight after animation completes
      const timer = setTimeout(() => {
        setHighlightedId(null);
      }, 2000); // 2 seconds for 2 pulses

      return () => clearTimeout(timer);
    }
  }, [requestId, session]);

  const formatDateTime = (
    date: number | undefined,
    prevDate: number | undefined,
    nextDate: number | undefined
  ) => {
    if (!date) return { display: "", full: "" };
    const currentMoment = moment(date);
    const prevMoment = prevDate ? moment(prevDate) : null;
    const nextMoment = nextDate ? moment(nextDate) : null;

    const showFullDate =
      (prevMoment && !currentMoment.isSame(prevMoment, "day")) ||
      (nextMoment && !currentMoment.isSame(nextMoment, "day"));

    const fullDateTime = currentMoment.format("MMM D, YYYY HH:mm:ss A");
    const displayDateTime = showFullDate
      ? fullDateTime
      : currentMoment.format("HH:mm:ss");

    return { display: displayDateTime, full: fullDateTime };
  };

  const handleViewRequestClick = (requestId: string) => {
    window.open(
      `/application/${applicationId}/session/${encodeURIComponent(
        sessionId
      )}/request/${requestId}`,
      "_blank"
    );
  };

  const loadTestSets = () => {
    TestSetService.getTestSets(null, apiKey).subscribe(
      (response: any) => {
        // response.items is already the array of test sets
        const testSets = response || [];
        // Group test sets by application_action_id
        const groupedTestSets = testSets.reduce(
          (acc: Record<string, TestSet[]>, testSet: TestSet) => {
            const actionType = testSet?.intent_group?.intent_name;
            if (!acc[actionType]) {
              acc[actionType] = [];
            }
            acc[actionType].push(testSet);
            return acc;
          },
          {}
        );

        setTestSetsByAction(groupedTestSets);
      },
      (error: any) => {
        console.error("Failed to load test sets:", error);
      }
    );
  };

  const handleTestSetUpdate = (
    updatedRequestId: string,
    updatedTestSetIds: string[]
  ) => {
    setSession((prevSession) => {
      if (!prevSession) return prevSession;

      const updatedTimeline = prevSession.timeline.map((t) => {
        if (t.request?.request_id === updatedRequestId) {
          return {
            ...t,
            request: {
              ...t.request,
              metadata: {
                ...t.request.metadata,
                test_set_ids: JSON.stringify(updatedTestSetIds),
              },
            },
          };
        }
        return t;
      });

      return {
        ...prevSession,
        timeline: updatedTimeline,
      };
    });
    loadTestSets();
  };

  const fetchRequestSummary = (turn: Turn) => {
    const requestId = turn.request?.request_id;
    if (!requestId || requestMetadata[requestId]) return;

    setSummaryLoading((prev) => ({ ...prev, [requestId]: true }));

    RequestsService.getRequestMetadata(turn.request, null, apiKey).subscribe(
      (result: any) => {
        if (result.request_id) {
          setRequestMetadata((prev) => ({
            ...prev,
            [result.request_id]: result.metadata,
          }));
        }
        setSummaryLoading((prev) => ({ ...prev, [requestId]: false }));
      },
      (error: any) => {
        console.error("Failed to fetch metadata:", error);
        setSummaryLoading((prev) => ({ ...prev, [requestId]: false }));
      }
    );
  };

  const getExistingTestSetIds = (metadata?: any) => {
    try {
      return metadata?.test_set_ids
        ? JSON.parse(metadata.test_set_ids).map(String)
        : [];
    } catch (e) {
      console.error("Error parsing test_set_ids:", e);
      return [];
    }
  };

  const updateAvailableIntents = React.useCallback((timeline: Turn[]) => {
    const newIntents = new Set<string>();
    timeline.forEach((turn) => {
      if (turn.request?.action_type) {
        newIntents.add(turn.request.action_type);
      }
    });

    setAvailableIntents((prev) => {
      const allIntents = Array.from(
        new Set([...prev, ...Array.from(newIntents)])
      );
      return allIntents.sort();
    });
  }, []);

  // Update available intents whenever the timeline changes
  useEffect(() => {
    if (session?.timeline) {
      updateAvailableIntents(session.timeline);
    }
  }, [session?.timeline, updateAvailableIntents]);

  // Convert available intents to options for multi-select
  const intentOptions: MultiSelectOption[] = availableIntents.map((intent) => ({
    label: intent,
    value: intent,
  }));

  // Get selected intents (those NOT in unselectedIntents)
  const selectedIntents = availableIntents.filter(
    (intent) => !unselectedIntents.has(intent)
  );

  // Handle intent selection changes from multi-select
  const handleIntentSelectionChange = (selectedValues: (string | number)[]) => {
    const selectedStrings = selectedValues.map(String);
    const newUnselected = new Set(
      availableIntents.filter((intent) => !selectedStrings.includes(intent))
    );
    setUnselectedIntents(newUnselected);
  };

  // Update the filtered timeline to exclude unselected intents
  const filteredTimeline = React.useMemo(() => {
    return sortedTimeline.filter(
      (turn) =>
        turn.request?.action_type &&
        !unselectedIntents.has(turn.request.action_type)
    );
  }, [sortedTimeline, unselectedIntents]);

  if (isLoading && session === null) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-8 py-8 space-y-8">
          {/* Header Card Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="h-4 bg-muted rounded w-32 animate-pulse mb-6" />
              <div className="space-y-2">
                <div className="h-8 bg-muted rounded w-64 animate-pulse" />
                <div className="h-5 bg-muted rounded w-96 animate-pulse" />
              </div>
            </CardContent>
          </Card>

          {/* Content Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="h-4 bg-muted rounded flex-1 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-8 animate-pulse" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const getFaultSummary = (turn: Turn) => {
    let faultSummary = turn.eval_request?.eval_results_set?.evaluate_summary;
    let suggestedCorrection = "";
    turn?.eval_request?.eval_results_set?.evaluation_results?.forEach(
      (val: EvaluateResult) => {
        if (!faultSummary && val.status == "FAULT") {
          faultSummary = val.description;
        }
        if (!suggestedCorrection && val.status == "FAULT") {
          suggestedCorrection = val.meta.correction as string;
        }
      }
    );
    
    return (
      <div className="space-y-3">
        <div className="space-y-1">
          <p className="text-sm font-gc-med text-foreground">Reason</p>
          <p className="text-sm text-foreground">{faultSummary}</p>
        </div>
        <div className="space-y-1">
          <p className="text-sm font-gc-med text-foreground">
            {turn.response?.correction_applied ? "Original" : "Suggested Correction"}
          </p>
          <div className="text-sm text-foreground">
            {formatResponseContent(
              turn.response?.correction_applied
                ? turn.eval_request?.text_content
                : suggestedCorrection
            )}
          </div>
        </div>
      </div>
    );
  };

  const content = (
    <div className="session-container" ref={timelineRef}>
      {/* Header */}
      {!propSessionId && !propApplicationId && (
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  {applicationId ? (
                    <button 
                      onClick={() => window.location.href = `/application/${applicationId}`}
                      className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                    >
                      Application
                    </button>
                  ) : (
                    <span className="text-muted-foreground font-gc-reg">Application</span>
                  )}
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">
                    Session {formatSessionId(session?.session_id || "")}
                  </span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                Session Details
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                View session details, requests, and messages
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Session Information Section */}
      <div className="space-y-6">
        {session ? (
          <>
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <HistoryIcon className="w-4 h-4 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground font-gc-reg">Session ID</p>
                </div>
                <div className="flex items-center gap-2">
                  <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground">
                    {formatSessionId(session?.session_id)}
                  </code>
                  <Tooltip content="Copy Session ID">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => {
                        navigator.clipboard.writeText(session?.session_id || "");
                      }}
                    >
                      <CopyIcon className="w-3 h-3" />
                    </Button>
                  </Tooltip>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <ClockIcon className="w-4 h-4 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground font-gc-reg">Date Created</p>
                </div>
                <p className="text-sm font-gc-reg text-foreground">
                  {moment(session?.date_created).format("MM/DD/YYYY hh:mm:ss A")}
                </p>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <DollarSignIcon className="w-4 h-4 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground font-gc-reg">Cost</p>
                </div>
                <p className="text-sm font-gc-reg text-foreground">
                  {session.cost > 0 ? formatCurrency(session.cost) : "N/A"}
                </p>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground font-gc-reg">Score</p>
                </div>
                <p className="text-sm font-gc-reg text-foreground">
                  {calculateScore(session)}
                </p>
              </div>
            </div>

            {/* Intent Filter */}
            {availableIntents.length > 0 && (
              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">
                  Filter Intents
                </Label>
                <MultiSelect
                  options={intentOptions}
                  value={selectedIntents}
                  onChange={handleIntentSelectionChange}
                  placeholder="Select intents to display..."
                  searchable={true}
                />
                {selectedIntents.length !== availableIntents.length && (
                  <p className="text-xs text-muted-foreground">
                    Showing {selectedIntents.length} of {availableIntents.length} intent types
                  </p>
                )}
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            Session still processing; please try again later
          </p>
        )}
      </div>

      {/* Timeline Section */}
      <div className="space-y-8 pt-8">
        <div className="flex items-center gap-2">
          <ClockIcon className="w-5 h-5 text-maitai-green" />
          <h2 className="text-lg font-gc-med text-foreground">Timeline</h2>
        </div>
        
        {/* Load More Button */}
        {filteredTimeline.length > 0 && !filteredTimeline[0].first && (
          <div className="flex items-center justify-center">
            <Button
              variant="outline"
              onClick={loadOlderRequests}
              disabled={isLoadingOlder}
              className="gap-2"
            >
              {isLoadingOlder ? (
                <Loader2Icon className="w-4 h-4 animate-spin" />
              ) : (
                <ArrowUpIcon className="w-4 h-4" />
              )}
              Load older requests
            </Button>
          </div>
        )}

        {/* Timeline Items */}
        <div className="space-y-6">
          {filteredTimeline.map((turn: Turn, index: number) => {
            const prevTurn = index > 0 ? filteredTimeline[index - 1] : null;
            const nextTurn =
              index < filteredTimeline.length - 1
                ? filteredTimeline[index + 1]
                : null;
            const { display: dateTimeString, full: fullDateTimeString } =
              formatDateTime(
                turn.request?.date_created,
                prevTurn?.request?.date_created,
                nextTurn?.request?.date_created
              );
            
            let faultySentinel = "";
            let faultCount = 0;
            turn.eval_request?.eval_results_set?.evaluation_results?.forEach(
              (evalResult: EvaluateResult) => {
                if (evalResult.status == "FAULT" && faultySentinel == "") {
                  faultySentinel = evalResult.sentinel_name || "";
                }
                if (evalResult.status == "FAULT") {
                  faultCount += 1;
                }
              }
            );
            
            const toolCalls =
              turn.response?.choices?.[0]?.message?.tool_calls?.map(
                (toolCall: ToolCall) => {
                  return (
                    <div key={toolCall.id} className="mt-3">
                      <div className="bg-muted/50 border border-border rounded-lg p-3">
                        <p className="text-xs text-muted-foreground mb-2">Tool Call</p>
                        <code className="text-sm text-foreground font-mono break-words whitespace-pre-wrap">
                          {toolCall?.function?.name}({toolCall?.function?.arguments})
                        </code>
                      </div>
                    </div>
                  );
                }
              );
            
            const requestMetadataLocal = JSON.parse(
              (turn.request?.metadata?._maitai_request_meta as string) || "{}"
            );

            return (
              <div
                key={turn.request?.request_id}
                data-request-id={turn.request?.request_id}
                className={cn(
                  "relative pl-8",
                  turn.request?.request_id === highlightedId && "animate-pulse"
                )}
              >
                {/* Timeline dot */}
                <div className="absolute left-0 top-6 flex items-center justify-center">
                  <div className={cn(
                    "w-3 h-3 rounded-full border-2 border-background",
                    faultCount > 0 
                      ? "bg-red-500" 
                      : turn.response?.correction_applied 
                      ? "bg-green-500" 
                      : "bg-maitai-green"
                  )}>
                  </div>
                  {/* Timeline line */}
                  {index < filteredTimeline.length - 1 && (
                    <div className="absolute top-6 left-1/2 w-px h-20 bg-border transform -translate-x-1/2"></div>
                  )}
                </div>

                <Card className={cn(
                  "border-border w-full",
                  turn.request?.request_id === highlightedId && "ring-2 ring-maitai-green/50"
                )}>
                  <Collapsible>
                    <CardContent className="p-6">
                      <div className="space-y-4">
                        {/* Timeline header */}
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                            <span className="text-xs text-muted-foreground">
                              {dateTimeString}
                            </span>
                            
                            <Badge variant="outline" className="text-xs">
                              {turn.request?.action_type}
                            </Badge>

                            {turn.response?.correction_applied ? (
                              <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 text-xs">
                                Corrected
                              </Badge>
                            ) : faultCount > 0 ? (
                              <Badge variant="destructive" className="text-xs">
                                {faultCount > 1 ? "FAULTS" : "FAULT"}
                              </Badge>
                            ) : (
                              <CheckCircleIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                            )}

                            {toolCalls?.length > 0 && (
                              <Badge variant="outline" className="text-xs">Tool Call</Badge>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="gap-1 text-xs h-7 px-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewRequestClick(turn.request?.request_id || "");
                              }}
                            >
                              <ExternalLinkIcon className="w-3 h-3" />
                              View
                            </Button>
                            
                            <Tooltip content="Copy Request ID">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyRequestId(turn.request?.request_id || "");
                                }}
                              >
                                <CopyIcon className="w-3 h-3" />
                              </Button>
                            </Tooltip>
                            
                            {turn.request && (
                              <TestSetContextMenu
                                applicationId={applicationId}
                                applicationActionType={turn.request.action_type || ""}
                                requestId={turn.request.request_id || ""}
                                testSets={
                                  turn.request.action_type
                                    ? [...(testSetsByAction[turn.request.action_type] || [])].sort((a, b) => b.id - a.id)
                                    : []
                                }
                                existingTestSetIds={getExistingTestSetIds(turn.request.metadata)}
                                onUpdate={handleTestSetUpdate}
                              >
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                >
                                  <PlusIcon className="w-3 h-3" />
                                </Button>
                              </TestSetContextMenu>
                            )}
                            
                            <CollapsibleTrigger className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-7 w-7 p-0">
                              <></>
                            </CollapsibleTrigger>
                          </div>
                        </div>

                        {/* Response content - now full width */}
                        <div className="space-y-3">
                          {requestMetadataLocal?.summary && (
                            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                              <div className="flex items-start gap-2">
                                <InfoIcon className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-blue-900 dark:text-blue-100">{requestMetadataLocal.summary}</p>
                              </div>
                            </div>
                          )}

                          <div className="space-y-2 min-w-0">
                            {turn.response?.choices?.[0]?.message?.content ? (
                              <div className="text-sm text-foreground leading-relaxed whitespace-pre-wrap break-words overflow-hidden">
                                {formatResponseContent(turn.response?.choices?.[0]?.message?.content)}
                              </div>
                            ) : (
                              <span className="text-muted-foreground italic text-sm">No response content</span>
                            )}
                            {turn.request?.error && (
                              <div className="p-2 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded text-red-900 dark:text-red-100 text-sm break-words">
                                {turn.request?.error}
                              </div>
                            )}
                          </div>

                          {toolCalls}
                        </div>

                        {/* Metadata row */}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          {turn.response?.response_time && turn.response.response_time > 0 && (
                            <span className="flex items-center gap-1">
                              <ClockIcon className="w-3 h-3" />
                              {turn.response?.response_time}ms
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <ServerIcon className="w-3 h-3" />
                            {turn.response?.usage?.prompt_tokens || 0} → {turn.response?.usage?.completion_tokens || 0} tokens
                          </span>
                          {turn.response?.usage?.cost && turn.response.usage.cost > 0 && (
                            <span className="flex items-center gap-1">
                              <DollarSignIcon className="w-3 h-3" />
                              {formatCurrency(turn.response.usage.cost)}
                            </span>
                          )}
                          {parseInt((turn?.request?.metadata?.reruns as string) || "0") > 0 && (
                            <span>{String(turn.request.metadata.reruns)} rerun{parseInt(turn.request.metadata.reruns as string) > 1 ? "s" : ""}</span>
                          )}
                        </div>

                        {/* Expandable technical details */}
                        <CollapsibleContent>
                          <div className="pt-4 border-t border-border">
                            <h4 className="text-sm font-gc-med text-foreground mb-3">Technical Details</h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                              <div className="space-y-1">
                                <p className="text-muted-foreground font-gc-reg">Inference</p>
                                <p className="text-foreground">
                                  {turn.request?.inference_location == "SERVER" ? "Server" : "Client"}
                                </p>
                              </div>
                              
                              <div className="space-y-1">
                                <p className="text-muted-foreground font-gc-reg">Model</p>
                                <p className="text-foreground">
                                  {turn.request?.params?.model || "-"}
                                </p>
                              </div>
                              
                              <div className="space-y-1">
                                <p className="text-muted-foreground font-gc-reg">Temperature</p>
                                <p className="text-foreground">
                                  {turn.request?.params?.temperature || "-"}
                                </p>
                              </div>
                              
                              <div className="space-y-1">
                                <p className="text-muted-foreground font-gc-reg">Streaming</p>
                                <p className="text-foreground">
                                  {turn.request?.params?.stream ? "On" : "Off"}
                                </p>
                              </div>
                              
                              <div className="space-y-1">
                                <p className="text-muted-foreground font-gc-reg">Evaluations</p>
                                <p className="text-foreground">
                                  {turn.eval_request?.id ? "On" : "Off"}
                                </p>
                              </div>
                              
                              <div className="space-y-1">
                                <p className="text-muted-foreground font-gc-reg">Corrections</p>
                                <p className="text-foreground">
                                  {turn.request?.apply_corrections ? "On" : "Off"}
                                </p>
                              </div>
                            </div>
                          </div>
                        </CollapsibleContent>
                      </div>
                    </CardContent>
                  </Collapsible>
                </Card>
              </div>
            );
          })}
        </div>

        {/* Listening indicator */}
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="w-2 h-2 bg-maitai-green rounded-full animate-pulse"></div>
            Listening for new requests...
          </div>
        </div>
      </div>
    </div>
  );

  // If both sessionId and applicationId are provided as props, render without header
  if (propSessionId && propApplicationId) {
    return (
      <div className="space-y-6">
        {content}
      </div>
    );
  }

  // Otherwise, wrap in full page layout
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-8 py-8 space-y-8">
        {content}
      </div>
    </div>
  );
};

export default SessionOverview;
